import type {
  AuftragAnsicht,
  Position,
  PositionEingabe,
  TaetigkeitsartAnsicht,
} from '@arbeitsrapporte/shared';
import { POSITIONSART_ERFASSBAR } from '@arbeitsrapporte/shared';
import { useIch } from '../App';
import { ART_TEXT } from '../format';

/** Eine Position im Formular – die Eingabe plus das, was zum Anzeigen festgehalten ist. */
export type Positionszeile = PositionEingabe & Pick<Position, 'artikelNummer'>;

/**
 * Eine neue Zeile – mit der Tätigkeitsart, die zur Person hinterlegt ist.
 *
 * Die Vorbelegung ist der Punkt: Ohne sie wählte der Techniker die Rolle bei
 * jeder Zeile neu, und dann wählt er irgendwann die falsche. Sie gilt nur
 * für `arbeit` und `reise` – Material ist frei benannt und wird nicht nach
 * Satz verrechnet.
 */
export function leerePosition(
  art: PositionEingabe['art'] = 'arbeit',
  vorbelegt: TaetigkeitsartAnsicht | null = null,
): Positionszeile {
  const passend = vorbelegt && vorbelegt.art === art ? vorbelegt : null;
  const einheit =
    passend?.einheit ?? { arbeit: 'h', reise: 'km', material: 'Stk', spesen: 'CHF' }[art];

  return {
    art,
    bezeichnung: '',
    /*
     * Stunden und Kilometer leer: „1 h" und „1 km" wären Vorbelegungen, die
     * niemand meint und die so ins ERP gingen. Bei Arbeit rechnet `von`/`bis`
     * die Menge aus; sonst trägt der Techniker sie ein. Material ist meist
     * ein Stück.
     */
    menge: art === 'material' ? '1' : '',
    einheit,
    von: null,
    bis: null,
    artikelId: null,
    artikelNummer: null,
    taetigkeitsartId: passend?.id ?? null,
    auftragspositionNummer: null,
    verrechenbar: true,
    bemerkung: '',
  };
}

/**
 * Die Art wechseln, ohne das Getippte zu verlieren.
 *
 * Bezeichnung, Bemerkung, Auftragsposition und „verrechenbar" bleiben –
 * wer „Reise" statt „Arbeit" wählt, hat die Zeile nicht falsch beschrieben,
 * nur falsch einsortiert. Menge, Einheit, Uhrzeiten und Rolle sind an die
 * Art gebunden und beginnen neu: 3.5 h sind keine 3.5 km.
 */
export function mitAndererArt(
  zeile: Positionszeile,
  art: PositionEingabe['art'],
  vorbelegt: TaetigkeitsartAnsicht | null,
): Positionszeile {
  return {
    ...leerePosition(art, vorbelegt),
    bezeichnung: zeile.bezeichnung,
    bemerkung: zeile.bemerkung,
    auftragspositionNummer: zeile.auftragspositionNummer,
    verrechenbar: zeile.verrechenbar,
  };
}

/** Stunden aus `von`/`bis`, auf Viertelstunden gerundet – ein Vorschlag, den man überschreiben darf. */
export function stundenAus(von: string | null, bis: string | null): string | null {
  if (!von || !bis) return null;

  const [vh, vm] = von.split(':').map(Number);
  const [bh, bm] = bis.split(':').map(Number);
  const minuten = (bh ?? 0) * 60 + (bm ?? 0) - ((vh ?? 0) * 60 + (vm ?? 0));

  if (minuten <= 0) return null;

  return String(Math.round(minuten / 15) / 4);
}

export function Positionen({
  zeilen,
  auftrag,
  taetigkeitsarten,
  onChange,
}: {
  zeilen: Positionszeile[];
  auftrag: AuftragAnsicht | null;
  /** Die der Gesellschaft dieses Rapports – die Artikelnummern von CH und DE sind verschieden. */
  taetigkeitsarten: TaetigkeitsartAnsicht[];
  onChange: (zeilen: Positionszeile[]) => void;
}) {
  /*
   * Die Vorbelegung der angemeldeten Person – nur, wenn sie zur Gesellschaft
   * dieses Rapports gehört. Wer für beide Gesellschaften arbeitet, hat eine
   * Vorbelegung für eine davon; bei der anderen wählt er selbst, statt
   * stillschweigend die falsche Nummer zu bekommen.
   */
  const ich = useIch();
  const vorbelegt = taetigkeitsarten.find((t) => t.id === ich.standardTaetigkeitsartId) ?? null;
  const setze = (i: number, teil: Partial<Positionszeile>) =>
    onChange(zeilen.map((z, j) => (j === i ? { ...z, ...teil } : z)));
  const entferne = (i: number) => onChange(zeilen.filter((_, j) => j !== i));

  return (
    <section className="mt-6">
      <h2 className="mb-2 text-lg font-semibold">Positionen</h2>

      {zeilen.length === 0 && (
        <p className="mb-3 text-slate-600">
          Noch keine Position. Ohne Position lässt sich der Rapport nicht abschließen.
        </p>
      )}

      <div className="space-y-3">
        {zeilen.map((z, i) => (
          /*
            Je Position ein `fieldset`: Es sind bis zu zehn Felder, die
            zusammengehören – ohne Rahmen liest sich das als ein langes
            Formular, und auf dem Telefon weiß man nach dem dritten Feld
            nicht mehr, zu welcher Zeile man scrollt.
          */
          <fieldset key={i} className="karte space-y-3">
            <div className="flex flex-wrap items-start gap-2">
              <select
                value={z.art}
                aria-label="Art"
                onChange={(e) =>
                  setze(i, mitAndererArt(z, e.target.value as PositionEingabe['art'], vorbelegt))
                }
                className="feld w-auto shrink-0"
              >
                {POSITIONSART_ERFASSBAR.map((a) => (
                  <option key={a} value={a}>
                    {ART_TEXT[a]}
                  </option>
                ))}
              </select>
              <input
                className="feld min-w-48 flex-1"
                placeholder="Bezeichnung"
                aria-label="Bezeichnung"
                required
                maxLength={200}
                value={z.bezeichnung}
                onChange={(e) => setze(i, { bezeichnung: e.target.value })}
              />
              <button
                type="button"
                onClick={() => entferne(i)}
                aria-label="Position entfernen"
                className="shrink-0 rounded px-3 text-slate-500 hover:bg-slate-100 hover:text-rose-800"
              >
                ✕
              </button>
            </div>

            {z.art === 'arbeit' && (
              <div className="grid grid-cols-2 gap-3 sm:max-w-xs">
                <div>
                  <label className="feldname" htmlFor={`von-${i}`}>
                    von
                  </label>
                  <Uhrzeit
                    id={`von-${i}`}
                    wert={z.von}
                    onChange={(von) => {
                      setze(i, { von, menge: stundenAus(von, z.bis) ?? z.menge });
                    }}
                  />
                </div>
                <div>
                  <label className="feldname" htmlFor={`bis-${i}`}>
                    bis
                  </label>
                  <Uhrzeit
                    id={`bis-${i}`}
                    wert={z.bis}
                    onChange={(bis) => {
                      setze(i, { bis, menge: stundenAus(z.von, bis) ?? z.menge });
                    }}
                  />
                </div>
              </div>
            )}

            {/*
             * Wofür verrechnet wird. Der Techniker wählt die Rolle, nicht die
             * Artikelnummer – die kennt er nicht und soll er nicht kennen
             * müssen. Ohne Wahl geht die Stunde ohne Artikel ins ERP; das
             * steht als Hinweis daneben, damit es nicht stumm passiert.
             */}
            {(z.art === 'arbeit' || z.art === 'reise') && (
              <div>
                <label className="feldname" htmlFor={`rolle-${i}`}>
                  verrechnet als
                </label>
                <select
                  id={`rolle-${i}`}
                  className="feld"
                  value={z.taetigkeitsartId ?? ''}
                  onChange={(e) => {
                    const gewaehlt = taetigkeitsarten.find((t) => t.id === e.target.value);

                    setze(i, {
                      taetigkeitsartId: gewaehlt?.id ?? null,
                      einheit: gewaehlt?.einheit ?? z.einheit,
                    });
                  }}
                >
                  <option value="">– keine Rolle –</option>
                  {taetigkeitsarten
                    .filter((t) => t.art === z.art)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.bezeichnung} ({t.artikelNummer})
                      </option>
                    ))}
                </select>
                {!z.taetigkeitsartId && (
                  <p className="mt-1 text-sm text-amber-700">
                    Ohne Rolle geht die Zeile ohne Artikelnummer ins ERP und ist dort nicht
                    verrechenbar.
                  </p>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <div className="w-28">
                <label className="feldname" htmlFor={`menge-${i}`}>
                  Menge
                </label>
                <input
                  id={`menge-${i}`}
                  inputMode="decimal"
                  required
                  pattern="\d{1,12}([.,]\d{1,3})?"
                  title="Zahl mit höchstens drei Nachkommastellen"
                  className="feld"
                  value={z.menge}
                  onChange={(e) => setze(i, { menge: e.target.value.replace(',', '.') })}
                />
              </div>
              <div className="w-28">
                <label className="feldname" htmlFor={`einheit-${i}`}>
                  Einheit
                </label>
                {/*
                  Mit gewählter Rolle bringt sie die Einheit mit, und die
                  bleibt: Aus „h" wird sonst „Std", und so ginge es an den Hub.
                */}
                <input
                  id={`einheit-${i}`}
                  className={`feld ${z.taetigkeitsartId ? 'bg-slate-50 text-slate-600' : ''}`}
                  required
                  readOnly={Boolean(z.taetigkeitsartId)}
                  title={z.taetigkeitsartId ? 'Die Einheit kommt aus der Rolle.' : undefined}
                  value={z.einheit}
                  onChange={(e) => setze(i, { einheit: e.target.value })}
                />
              </div>
              <label className="gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={z.verrechenbar}
                  onChange={(e) => setze(i, { verrechenbar: e.target.checked })}
                />
                verrechenbar
              </label>
            </div>

            {auftrag && auftrag.positionen.length > 0 && (
              <div>
                <label className="feldname" htmlFor={`auftragsposition-${i}`}>
                  Auftragsposition
                </label>
                <select
                  id={`auftragsposition-${i}`}
                  className="feld"
                  value={z.auftragspositionNummer ?? ''}
                  onChange={(e) =>
                    setze(i, {
                      auftragspositionNummer: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                >
                  <option value="">– keine –</option>
                  {auftrag.positionen.map((p) => (
                    <option key={p.nummer} value={p.nummer}>
                      {p.nummer}. {p.bezeichnung}
                      {p.menge ? ` (${p.menge} ${p.einheit})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <input
              className="feld"
              placeholder="Bemerkung (kommt auf den Rapport)"
              value={z.bemerkung}
              onChange={(e) => setze(i, { bemerkung: e.target.value })}
            />
          </fieldset>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {POSITIONSART_ERFASSBAR.map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => onChange([...zeilen, leerePosition(a, vorbelegt)])}
            className="knopf"
          >
            + {ART_TEXT[a]}
          </button>
        ))}
      </div>
    </section>
  );
}

/** Die Uhrzeiten zur Wahl: 00:00 bis 23:45, im Viertelstundentakt. */
const VIERTELSTUNDEN = Array.from({ length: 96 }, (_, n) => {
  const stunde = String(Math.floor(n / 4)).padStart(2, '0');
  const minute = String((n % 4) * 15).padStart(2, '0');
  return `${stunde}:${minute}`;
});

/**
 * Von und bis im Viertelstundentakt – eine Auswahl statt `type="time"`: Der
 * Uhrzeitwähler des Browsers bot jede Minute an, und `step` beachtet er im
 * Aufklappmenü nicht. Eine ältere Zeit außerhalb des Takts bleibt als
 * Eintrag stehen, damit sie beim Öffnen nicht still verschwindet.
 */
function Uhrzeit({
  id,
  wert,
  onChange,
}: {
  id: string;
  wert: string | null;
  onChange: (wert: string | null) => void;
}) {
  const zeiten =
    wert && !VIERTELSTUNDEN.includes(wert) ? [...VIERTELSTUNDEN, wert].sort() : VIERTELSTUNDEN;

  return (
    <select
      id={id}
      className="feld"
      value={wert ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">--:--</option>
      {zeiten.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}
